import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { switchMap, map, catchError, take, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Community } from "../../shared/models/community.model";
import { CachingService } from "../../shared/service/caching/caching.service";
import { CommunityCard } from "../../shared/models/community-card.model";
import { BusinessUnits } from "../../shared/models/BusinessUnits.models";
import { CommunityBaseModel } from "../../shared/models/communityBase.model";
import { UserService } from "../../core/authentication/services/user.service";
import { RolePermissionService } from "../../core/authentication/services/role-permission.service";

export interface MembershipChangeError {
  body: string;
  url: string;
  status: number;

  customMessage: string;
}

enum CacheEnum {
  COMMUNITY_DATA_CACHE_KEY = "communityData_",
  COMMUNITIES_OVERVIEW_DATA_CACHE_KEY = "communitiesOverviewData",
  COMMUNITIES_FROM_BASE_DATA_CACHE_KEY = "communitiesFromBaseData_",
  COMMUNITY_BASE_DATA_CACHE_KEY = "communityBaseData_",
}

@Injectable({
  providedIn: "root",
})
export class CommunityService {
  cacheEnum = CacheEnum;
  private loadingSubject = new BehaviorSubject<boolean>(true);

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private rolePermissionService: RolePermissionService,
    private cachingService: CachingService
  ) {}

  get loading(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  forceCacheRefresh(communityId: number = null, baseId: number = null) {
    if (communityId) {
      this.cachingService.clearKey(
        `${this.cacheEnum.COMMUNITY_DATA_CACHE_KEY}${communityId}`
      );
    }
    if (baseId) {
      this.cachingService.clearKey(
        `${this.cacheEnum.COMMUNITIES_FROM_BASE_DATA_CACHE_KEY}${baseId}`
      );
    }

    this.cachingService.clearKey(
      this.cacheEnum.COMMUNITIES_OVERVIEW_DATA_CACHE_KEY
    );
  }

  getCommunitiesOverviewData(
    forceRefresh: boolean = false
  ): Observable<Community[]> {
    const api = "api/community";

    return this.cachingService
      .getOrFetchData<Community[]>(
        api,
        this.cacheEnum.COMMUNITIES_OVERVIEW_DATA_CACHE_KEY,
        forceRefresh
      )
      .pipe(tap(() => this.loadingSubject.next(false)));
  }

  getCommunityData(
    communityId: number,
    forceRefresh: boolean = false
  ): Observable<Community> {
    const api = `api/community/${communityId}`;
    const cacheKey = `${this.cacheEnum.COMMUNITY_DATA_CACHE_KEY}${communityId}`;

    return this.cachingService
      .getOrFetchData<Community>(api, cacheKey, forceRefresh)
      .pipe(tap(() => this.loadingSubject.next(false)));
  }

  getCommunitiesFromBaseData(
    baseId: number,
    forceRefresh: boolean = false
  ): Observable<CommunityCard[]> {
    const api = `api/community/base/${baseId}`;
    const cacheKey = `${this.cacheEnum.COMMUNITIES_FROM_BASE_DATA_CACHE_KEY}${baseId}`;

    return this.cachingService
      .getOrFetchData<CommunityCard[]>(api, cacheKey, forceRefresh)
      .pipe(tap(() => this.loadingSubject.next(false)));
  }

  getCommunityBaseData(
    baseId: number,
    forceRefresh: boolean = false
  ): Observable<CommunityBaseModel> {
    const api = `api/communitybase/${baseId}`;
    const cacheKey = `${this.cacheEnum.COMMUNITY_BASE_DATA_CACHE_KEY}${baseId}`;

    return this.cachingService.getOrFetchData<CommunityBaseModel>(
      api,
      cacheKey,
      forceRefresh
    );
  }

  getBunits(): Observable<BusinessUnits[]> {
    return this.http.get<BusinessUnits[]>("api/bunit");
  }

  confirm(message: string): Observable<boolean> {
    return of(confirm(message));
  }

  public isCoreMemberOfCommunities(
    communityIds: number[]
  ): Observable<boolean> {
    return this.checkCoreMemberOfCommunity(communityIds, false);
  }

  public isCommunityLeadOfCommunities(
    communityIds: number[]
  ): Observable<boolean> {
    return this.checkCoreMemberOfCommunity(communityIds, true);
  }

  private checkCoreMemberOfCommunity(
    communityIds: number[],
    checkLead: boolean
  ): Observable<boolean> {
    if (!communityIds || communityIds.length === 0) return of(false);

    return this.rolePermissionService.hasCoreMemberAccess$.pipe(
      switchMap((allowed) =>
        !allowed
          ? of(false)
          : this.userService.userInfo$.pipe(
              map((user) =>
                user?.coreMemberOfCommunities.some(
                  (communityUserIsCoreMemberOf) =>
                    communityIds.some(
                      (communityId) =>
                        communityUserIsCoreMemberOf.communityId ===
                          communityId &&
                        (!checkLead ||
                          communityUserIsCoreMemberOf.responsibleForCommunity)
                    )
                )
              )
            )
      )
    );
  }

  /**
   * Toggles the current user's membership to the supplied community.
   * Returns an observable boolean indicating the new membership status.
   */
  toggleMembership(
    communityId: number,
    communityBaseId: number,
    isMember: boolean
  ): Observable<boolean> {
    const cantSubscribe404 =
        "Could not subscribe to community, the community does not exist or you are already member of the community",
      cantLeave404 =
        "Could not leave community, the community does not exist or you are not member of the community";
    return this.isCoreMemberOfCommunities([communityId]).pipe(
      take(1),
      switchMap((isCoreMember) =>
        !isMember || !isCoreMember
          ? of(true)
          : this.confirm(
              "This will remove you as a core member of this community. " +
                "Are you sure you want to do this?"
            )
      ),
      switchMap((confirmationOk) => {
        if (!confirmationOk) return of(isMember);

        return (
          isMember
            ? this.http.delete(`api/communitymembership/${communityId}`)
            : this.http.put(`api/communitymembership/${communityId}`, {})
        ).pipe(
          tap(() => {
            this.forceCacheRefresh(communityId, communityBaseId);
          }),
          map(() => !isMember)
        );
      }),
      catchError((err: MembershipChangeError) => {
        err.customMessage =
          err.status !== 404
            ? "Something went wrong"
            : isMember
            ? cantLeave404
            : cantSubscribe404;

        return throwError(() => err);
      })
    );
  }
}
